import { orchestrator } from 'satcheljs';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import {
    getComposeEvent,
    getOriginalEvent,
    getFullComposeViewState,
} from 'owa-calendar-compose-store-manager';
import getCalendarOwnerFromItem from 'owa-calendar-forms-common/lib/utils/getCalendarOwnerFromItem';
import { createEnhancedLocationFromRoomInfo, isRoom } from 'owa-location-persona';
import { getUserConfiguration } from 'owa-session-store';
import {
    setShowSchedulingAssistant,
    setShowDateSelectorTimeZones,
    logComposeFormOpenedTimes,
    updateDateTimes,
    updateRequiredAttendees,
    updateOptionalAttendees,
    updateLocations,
    updateAllDay,
    openComposeSchedulingAssistant,
    handleSchedulingAssistantSave,
    handleSchedulingAssistantDiscard,
    closeComposeSchedulingAssistant,
    updateSidePaneType,
} from 'owa-calendar-compose-form-actions';
import {
    lazyInitializeSchedulingAssistant,
    lazyResetSchedulingAssistant,
} from 'owa-calendar-scheduling-assistant';
import { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import { logChangeTimeActions } from 'owa-calendar-compose-form-actions/lib/actions/eventMetricsActions';
import { isFTDFSeriesMaster, isEventAMeeting } from 'owa-calendar-event-capabilities';
import type { OwaDate } from 'owa-datetime';
import resolveStringLocation from '../utils/resolveStringLocation';
import { updateCollabSpace } from '../utils/updateCollabSpace';
import { getSchedulingAssistantViewStateId } from 'owa-calendar-compose-form-store/lib/selectors/getSchedulingAssistantViewStateId';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';

orchestrator(openComposeSchedulingAssistant, async actionMessage => {
    const { formId } = actionMessage;

    const event = getComposeEvent(formId);
    const fullComposeViewState = getFullComposeViewState(formId);

    // Close all side panes when we open scheduling assistant
    updateSidePaneType(formId, SidePaneType.None);

    const { originalStart, originalEnd, originalIsAllDayEvent } = getOriginalEventTime(formId);

    await lazyInitializeSchedulingAssistant.importAndExecute({
        viewStateId: getSchedulingAssistantViewStateId(formId),
        composeFormId: formId,
        start: event.Start,
        end: event.End,
        isAllDayEvent: event.IsAllDayEvent,
        originalEventStart: originalStart,
        originalEventEnd: originalEnd,
        originalEventIsAllDayEvent: originalIsAllDayEvent,
        requiredAttendees: event.RequiredAttendees,
        optionalAttendees: event.OptionalAttendees,
        resources: event.Resources,
        organizer: {
            // For new calendar items, Organizer might not be set yet,
            Mailbox: event.Organizer?.Mailbox || getCalendarOwnerFromItem(event),
            ResponseType: 'Organizer',
        },
        isOrganizer: event.IsOrganizer,
        isProposeNewTime: false,
        isAcceptProposeNewTime: fullComposeViewState.isAcceptProposeNewTime,
        canModifyPersonAttendees: canModifyPersonAttendees(formId),
        canModifyResourceAttendees: event.IsOrganizer,
        colorScheme: getComposeCalendarColorScheme(formId),
        recurrence: event.Recurrence,
        parentFolderId: event.ParentFolderId,
        shouldAdjustStartTimeEndTime: fullComposeViewState.shouldAdjustStartTimeEndTime,
        isAllDayEventExplicitlySet: fullComposeViewState.isAllDayEventExplicitlySet,
    });

    resolveStringLocation(fullComposeViewState.unresolvedLocation, formId);

    logComposeFormOpenedTimes(formId);

    // Update the calendar event to set the collaborative space from the collaborative space store
    await updateCollabSpace(formId);

    setShowSchedulingAssistant(formId, true);
});

orchestrator(handleSchedulingAssistantSave, actionMessage => {
    const { formId, finalState, isAllDayEventExplicitlySet } = actionMessage;

    const event = getComposeEvent(formId);

    const userTimeZone = getUserConfiguration().UserOptions.TimeZone;
    updateAllDay(finalState.IsAllDayEvent, isAllDayEventExplicitlySet, formId);
    updateDateTimes(
        formId,
        {
            start: finalState.Start,
            end: finalState.End,
        },
        TimeChangeActionType.SchedulingAssistantSaveChanges
    );

    setShowDateSelectorTimeZones(
        !finalState.IsAllDayEvent &&
            (finalState.Start.tz != userTimeZone || finalState.End.tz != userTimeZone),
        formId
    );

    updateRequiredAttendees(finalState.RequiredAttendees, formId);
    updateOptionalAttendees(finalState.OptionalAttendees, formId);

    // Location well
    // filter rooms from locations and add the locations from SA
    let locations = event.Locations.filter(location => !isRoom(location.PostalAddress));

    finalState.Resources.forEach(value =>
        locations.push(
            createEnhancedLocationFromRoomInfo(value.Mailbox.Name, value.Mailbox.EmailAddress)
        )
    );

    updateLocations(locations, formId);

    logComposeFormOpenedTimes(formId);

    closeComposeSchedulingAssistant(formId);
});

orchestrator(handleSchedulingAssistantDiscard, actionMessage => {
    const { formId } = actionMessage;

    logChangeTimeActions(TimeChangeActionType.SchedulingAssistantDiscardChanges, formId);

    logComposeFormOpenedTimes(formId);

    closeComposeSchedulingAssistant(formId);
});

orchestrator(closeComposeSchedulingAssistant, actionMessage => {
    const { formId } = actionMessage;

    lazyResetSchedulingAssistant.importAndExecute(
        getSchedulingAssistantViewStateId(formId) /* viewStateId */
    );

    if (getFullComposeViewState(formId)) {
        setShowSchedulingAssistant(formId, false);
    }
});

function getOriginalEventTime(
    formId: string
): {
    originalStart: OwaDate;
    originalEnd: OwaDate;
    originalIsAllDayEvent: boolean;
} {
    const originalEvent = getOriginalEvent(formId);

    return {
        originalStart: originalEvent.Start,
        originalEnd: originalEvent.End,
        originalIsAllDayEvent: originalEvent.IsAllDayEvent,
    };
}

function canModifyPersonAttendees(formId: string): boolean {
    const event = getComposeEvent(formId);
    const originalEvent = getOriginalEvent(formId);

    // FTDF series can not be modified.  However if there are no existing attendees, new
    // ones can be added
    return !(isFTDFSeriesMaster(event) && isEventAMeeting(originalEvent)) && event.IsOrganizer;
}
